import React, { useEffect, useState } from "react";
import { Plus, Trash2, Archive, RotateCcw, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatTRY } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MoneyInput from "@/components/MoneyInput";

const STATUS_TONE = {
  taslak: "bg-secondary text-muted-foreground",
  onaylandi: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
  odendi: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
};
const STATUS_LABEL = { taslak: "Taslak", onaylandi: "Onaylandı", odendi: "Ödendi" };

export default function Hakedis() {
  const { hasPerm } = useAuth();
  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);
  const [archived, setArchived] = useState([]);
  const [projects, setProjects] = useState([]);
  const [cariler, setCariler] = useState([]);
  const [subcontracts, setSubcontracts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const now = new Date();
  const emptyForm = {
    project_id: "", cari_id: "", amount: 0,
    period: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
    description: "", status: "taslak", subcontract_id: "",
  };
  const [form, setForm] = useState(emptyForm);

  const resetForm = () => { setForm(emptyForm); setEditingId(null); };
  const openCreate = () => { resetForm(); setOpen(true); };
  const openEdit = (h) => {
    setEditingId(h.id);
    setForm({
      project_id: h.project_id || "",
      cari_id: h.cari_id || "",
      amount: h.amount || 0,
      period: h.period || emptyForm.period,
      description: h.description || "",
      status: h.status || "taslak",
      subcontract_id: h.subcontract_id || "",
    });
    setOpen(true);
  };

  const load = async () => {
    const [{ data: h }, { data: ah }, { data: p }, { data: c }, { data: sc }] = await Promise.all([
      api.get("/hakedis?archived=false"),
      api.get("/hakedis?archived=true"),
      api.get("/projects"),
      api.get("/cariler"),
      api.get("/subcontracts?archived=false").catch(() => ({ data: [] })),
    ]);
    setItems(h); setArchived(ah); setProjects(p); setCariler(c); setSubcontracts(sc);
  };

  useEffect(() => { load(); }, []);

  const shown = tab === "archive" ? archived : items;
  const total = shown.reduce((s, i) => s + i.amount, 0);

  // Subcontracts filtered to current cari+project selection
  const filteredSubs = subcontracts.filter(
    (s) => (!form.cari_id || s.cari_id === form.cari_id) && (!form.project_id || s.project_id === form.project_id)
  );

  const create = async () => {
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        subcontract_id: form.subcontract_id || null,
      };
      if (editingId) {
        await api.patch(`/hakedis/${editingId}`, payload);
        toast.success("Hakediş güncellendi");
      } else {
        await api.post("/hakedis", payload);
        toast.success("Hakediş kaydedildi");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu hakedişi arşive taşımak istediğinize emin misiniz?")) return;
    await api.delete(`/hakedis/${id}`);
    toast.success("Arşive taşındı");
    load();
  };

  const restore = async (id) => {
    await api.post(`/hakedis/${id}/restore`);
    toast.success("Geri getirildi");
    load();
  };

  const purge = async (id) => {
    if (!window.confirm("Bu hakedişi KALICI olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.")) return;
    await api.delete(`/hakedis/${id}/purge`);
    toast.success("Kalıcı olarak silindi");
    load();
  };

  return (
    <div className="space-y-6" data-testid="hakedis-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Şantiye</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Hakedişler</h1>
        </div>
        {hasPerm("hakedis.create") && (
          <Button data-testid="new-hakedis-btn" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" /> Yeni Hakediş
          </Button>
        )}
      </div>

      <Card className="p-4 shadow-none rounded-md border-l-4 border-l-brand max-w-xs">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">
          {tab === "archive" ? "Arşiv Toplamı" : "Toplam Hakediş"}
        </div>
        <div className="font-heading text-2xl font-bold tabular-nums">{formatTRY(total)}</div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="hakedis-tabs">
          <TabsTrigger value="active" data-testid="tab-active">Aktif ({items.length})</TabsTrigger>
          <TabsTrigger value="archive" data-testid="tab-archive"><Archive className="w-3 h-3 mr-1" /> Arşiv ({archived.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="shadow-none rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Dönem</TableHead>
                <TableHead>Proje</TableHead>
                <TableHead>Cari (Taşeron)</TableHead>
                <TableHead>Açıklama</TableHead>
                <TableHead>Kaydeden</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="text-right">Tutar</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((h) => (
                <TableRow key={h.id} data-testid={`hakedis-row-${h.id}`} className={`hover:bg-secondary/40 ${tab === "archive" ? "opacity-70" : ""}`}>
                  <TableCell className="text-sm tabular-nums font-medium">{h.period}</TableCell>
                  <TableCell className="text-sm">{projects.find((p) => p.id === h.project_id)?.name || "-"}</TableCell>
                  <TableCell className="text-sm">{cariler.find((c) => c.id === h.cari_id)?.name || "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{h.description || "-"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{h.created_by_name || "-"}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold ${STATUS_TONE[h.status]}`}>
                      {STATUS_LABEL[h.status]}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{formatTRY(h.amount)}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {tab === "active" && hasPerm("hakedis.edit") && (
                        <Button size="sm" variant="ghost" onClick={() => openEdit(h)} className="h-8 w-8 p-0" title="Düzenle" data-testid={`hakedis-edit-${h.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                      {tab === "active" && hasPerm("hakedis.delete") && (
                        <Button size="sm" variant="ghost" onClick={() => remove(h.id)} className="h-8 w-8 p-0 text-destructive" title="Arşive Taşı">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                      {tab === "archive" && hasPerm("hakedis.edit") && (
                        <Button size="sm" variant="ghost" onClick={() => restore(h.id)} className="h-8 w-8 p-0" title="Geri Getir" data-testid={`hakedis-restore-${h.id}`}>
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                      )}
                      {tab === "archive" && hasPerm("hakedis.delete") && (
                        <Button size="sm" variant="ghost" onClick={() => purge(h.id)} className="h-8 w-8 p-0 text-destructive" title="Kalıcı Sil" data-testid={`hakedis-purge-${h.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {shown.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">{tab === "archive" ? "Arşivde hakediş yok." : "Hakediş yok."}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Hakediş Düzenle" : "Yeni Hakediş"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Proje</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                  <SelectTrigger data-testid="hakedis-project"><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Taşeron/Cari</Label>
                <Select value={form.cari_id} onValueChange={(v) => setForm({ ...form, cari_id: v })}>
                  <SelectTrigger data-testid="hakedis-cari"><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent>{cariler.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Dönem (YYYY-MM)</Label><Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} /></div>
              <div className="space-y-2"><Label>Tutar</Label><MoneyInput data-testid="hakedis-amount" value={form.amount} onChange={(n) => setForm({ ...form, amount: n })} /></div>
            </div>
            <div className="space-y-2"><Label>Bağlı Sözleşme (opsiyonel)</Label>
              <Select value={form.subcontract_id || "none"} onValueChange={(v) => setForm({ ...form, subcontract_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="hakedis-subcontract"><SelectValue placeholder="Sözleşme seç" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Sözleşmesiz —</SelectItem>
                  {filteredSubs.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.contract_no} · {s.work_type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.cari_id && form.project_id && filteredSubs.length === 0 && (
                <p className="text-[11px] text-muted-foreground">Bu cari ve proje için sözleşme bulunamadı.</p>
              )}
            </div>
            <div className="space-y-2"><Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="taslak">Taslak</SelectItem>
                  <SelectItem value="onaylandi">Onaylandı</SelectItem>
                  <SelectItem value="odendi">Ödendi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Açıklama</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="hakedis-save" onClick={create} disabled={!form.amount || !form.project_id || !form.cari_id}>{editingId ? "Kaydet" : "Ekle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
